import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

// Use environment variables provided by Vite
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://embqetqnbgbpzkekvuhi.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVtYnFldHFuYmdicHprZWt2dWhpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzg5NTg1NzcsImV4cCI6MjA1NDUzNDU3N30.om_wXs8emRG4ooRN1NCtAxbs-CcLZ9gXOeQTR1gGxkI";

export const supabase = createClient<Database>(
  SUPABASE_URL, 
  SUPABASE_PUBLISHABLE_KEY,
  {
    auth: {
      persistSession: true,
      storageKey: 'supabase.auth.token',
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
  }
);

// Email service using Supabase Edge Function
export const emailService = {
  sendEmail: async ({
    to,
    subject,
    html,
    from,
    text,
    replyTo,
    cc,
    bcc
  }: {
    to: string | string[];
    subject: string;
    html: string;
    from?: string;
    text?: string;
    replyTo?: string;
    cc?: string[];
    bcc?: string[];
  }) => {
    try {
      console.log(`🔍 EMAIL CLIENT: Sending email to: ${typeof to === 'string' ? to : to.join(', ')}`);
      console.log(`🔍 EMAIL CLIENT: Subject: ${subject}`);
      console.log(`🔍 EMAIL CLIENT: From: ${from || 'default'}`);
      
      const requestBody = {
        to,
        subject,
        html,
        from: from || 'LeadExtract <leads@notifications.leadextract.io>',
        text,
        replyTo,
        cc,
        bcc
      };
      
      console.log("🔍 EMAIL CLIENT: Invoking send-email function with payload size:", 
        JSON.stringify(requestBody).length, "bytes");
      
      const startTime = Date.now();
      
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: requestBody
      });
      
      const duration = Date.now() - startTime;
      console.log(`🔍 EMAIL CLIENT: Function invocation took ${duration}ms`);

      if (error) {
        console.error('🔍 EMAIL CLIENT ERROR: Error from send-email function:', error);
        console.error('🔍 EMAIL CLIENT ERROR: Full error details:', JSON.stringify(error));
        return { data: null, error };
      }
      
      // Check for Resend API errors in the response
      if (data?.error) {
        console.error('🔍 EMAIL CLIENT ERROR: Resend API error:', data.error);
        console.error('🔍 EMAIL CLIENT ERROR: Full Resend error details:', JSON.stringify(data.error));
        return { data: null, error: data.error };
      }
      
      console.log('🔍 EMAIL CLIENT: Email sent successfully:', data);
      return { data, error: null };
    } catch (error) {
      console.error('🔍 EMAIL CLIENT ERROR: Exception in sendEmail:', error);
      console.error('🔍 EMAIL CLIENT ERROR: Error stack:', error?.stack);
      return { data: null, error };
    }
  },
  
  sendInvitation: async ({
    to,
    inviterName,
    inviteToken,
    role
  }: {
    to: string;
    inviterName?: string;
    inviteToken: string;
    role?: string;
  }) => {
    try {
      if (!to) {
        console.error('Missing recipient email address');
        throw new Error('Missing recipient email address');
      }
      
      if (!inviteToken) {
        console.error('Missing invite token');
        throw new Error('Missing invite token');
      }
      
      console.log(`Sending invitation email to: ${to} with token length: ${inviteToken.length}`);
      
      const { data, error } = await supabase.functions.invoke('send-invite-email', {
        body: {
          to,
          inviterName,
          inviteToken,
          role: role || 'member'
        }
      });

      if (error) {
        console.error('Error from send-invite-email function:', error);
        throw error;
      }
      
      // Check for Resend API errors in the response
      if (data?.error) {
        console.error('Resend API error in invitation:', data.error);
        return { data: null, error: data.error };
      }
      
      console.log('Invitation email sent successfully:', data);
      return { data, error: null };
    } catch (error) {
      console.error('Exception in sendInvitation:', error);
      return { data: null, error };
    }
  }
};
